import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '@yucanvas/shared';
import { supabase } from '../lib/supabase';
import { fetchProfile } from '../data/reads';
import { changeMyPassword, passwordChangeRequired } from '../data/accounts';
import { clearLocalData, pendingCount } from '../offline/db';
import { syncNow } from '../offline/sync';

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  /**
   * True while they are signed in on a temporary password an organizer reset
   * for them. The shell shows nothing else until they choose their own. Null
   * until the database has answered, so the turf list does not flash first.
   */
  mustChangePassword: boolean | null;
  changePassword: (password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        setSession(data.session);
        setLoading(false);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // The profile row -- not auth.users -- is what every RLS policy reads, so
  // the app needs it before it can render anything turf-shaped.
  useEffect(() => {
    const userId = session?.user.id;
    if (!userId) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    fetchProfile(userId)
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? `Could not load your profile: ${cause.message}`
              : 'Could not load your profile.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [session?.user.id]);

  // Asked once per sign-in, alongside the profile. The answer comes from the
  // database comparing password hashes (20260927000100), so it cannot be
  // talked out of by anything the client does short of changing the password.
  useEffect(() => {
    const userId = session?.user.id;
    if (!userId) {
      setMustChangePassword(false);
      return;
    }
    setMustChangePassword(null);
    let cancelled = false;
    void passwordChangeRequired().then((required) => {
      if (!cancelled) setMustChangePassword(required);
    });
    return () => {
      cancelled = true;
    };
  }, [session?.user.id]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      profile,
      loading,
      error,
      mustChangePassword,
      async changePassword(password) {
        await changeMyPassword(password);
        setMustChangePassword(await passwordChangeRequired());
      },
      async signIn(email, password) {
        setError(null);
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;
      },
      async signOut() {
        // Signing out wipes the device store -- a shared phone must not carry
        // one volunteer's turf into another's session. So the queue has to be
        // empty first: otherwise handing the phone over at the end of a shift
        // silently destroys a day of knocks (PLAN 5.7).
        if ((await pendingCount()) > 0) {
          await syncNow({ retryRejected: true });
          if ((await pendingCount()) > 0) {
            throw new Error(
              'Some visits have not uploaded yet. Find signal and sync before signing out.',
            );
          }
        }

        await supabase.auth.signOut();
        await clearLocalData();
        setProfile(null);
      },
    }),
    [session, profile, loading, error, mustChangePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
